import Followable from "./mixins/followable";
import smoothPan from "@/utils/maps/smooth-pan";
import { Geocodable } from "kinnect-mixins";
import { MapElementMixin, gmapApi } from "vue2-google-maps";

export default {
	name: "KBaseMapMarker",

	provide() {
		const markerPosition = {};

		// Ensure reactivity
		Object.defineProperties( markerPosition, {
			base: {
				get: () => this.position
			},
			spiderfied: {
				get: () => this.spiderfiedPosition
			}
		} );

		return { markerPosition };
	},

	inject: [ "storeModuleName" ],

	mixins: [
		Followable,
		Geocodable,
		MapElementMixin
	],

	props: {
		autoPan: Boolean,

		markerId: {
			required: true,
			type: [ Number, String ]
		},

		markerType: {
			required: true,
			type: String
		},

		icon: [ String, Object ],

		lat: {
			required: true,
			type: Number
		},

		lng: {
			required: true,
			type: Number
		},

		// Is the child of another marker
		isChildMarker: Boolean,

		// Whether or not to return geocoding information to active slots
		enableGeolocation: Boolean,

		// Apply a slight randomization to marker position to help with
		// markers exactly overlapping
		randomizePosition: Boolean,

		title: String,

		zIndex: Number
	},

	data: () => ( {
		panToActive: false,
		mapReady: false
	} ),

	computed: {
		google: gmapApi,

		locationToGeocode() {
			return this.position;
		},

		position() {
			if ( this.randomizePosition ) {
				const radius = 0.0001 * Math.random();
				const angleRadians = 2 * Math.PI * Math.random();
				const deltaX = radius * Math.cos( angleRadians );
				const deltaY = radius * Math.sin( angleRadians );

				return {
					lat: parseFloat( ( this.lat + deltaY ).toFixed( 6 ) ),
					lng: parseFloat( ( this.lng + deltaX ).toFixed( 6 ) )
				};
			}


			return {
				lat: this.lat,
				lng: this.lng
			};
		},

		spiderfiedPosition() {
			return this.$refs[this.markerId]
				? this.$refs[this.markerId].$markerObject.position
				: this.position;
		},

		spiderfier() {
			return this.$store.state[this.storeModuleName].spiderfier;
		},

		activeMarker: {
			get() {
				return this.$store.state[this.storeModuleName].activeMarker;
			},
			set( markerData ) {
				this.$store.dispatch( `${this.storeModuleName}/setActiveMarker`, markerData );
			}
		},

		rootActiveMarker: {
			get() {
				return this.$store.state[this.storeModuleName].rootActiveMarker;
			},
			set( markerData ) {
				this.$store.dispatch( `${this.storeModuleName}/setRootActiveMarker`, markerData );
			}
		},

		rootActive() {
			return this.markerId === this.rootActiveMarker.id && this.markerType === this.rootActiveMarker.type;
		},

		editMode() {
			return this.$store.state[this.storeModuleName].editMode;
		},

		active: {
			get() {
				return this.markerId === this.activeMarker.id && this.markerType === this.activeMarker.type;
			},
			set( active ) {
				let markerData = null;

				if ( active ) {
					markerData = {
						id: this.markerId,
						type: this.markerType
					};
				}

				if ( this.isChildMarker ) {
					this.activeMarker = markerData;
				} else {
					this.rootActiveMarker = markerData;
				}
			}
		},

		mapCenter: {
			get() {
				return this.$store.state[this.storeModuleName].mapCenter;
			},
			set( center ) {
				this.$store.dispatch( `${this.storeModuleName}/setMapCenter`, center );
			}
		},

		mapZoom() {
			return this.$store.state[this.storeModuleName].mapZoom;
		},

		centered() {
			return this.position
				&& this.mapCenter.lat === this.position.lat
				&& this.mapCenter.lng === this.position.lng;
		},

		highlightRadius() {
			return 2e6 / Math.pow( 2, this.mapZoom - 1 );
		},

		genHighlight() {
			if ( this.rootActive || this.active ) {
				return <gmap-circle
					center={this.spiderfiedPosition}
					radius={this.highlightRadius}
					options={{
						clickable: false,
						fillColor: "#36A1FC",
						fillOpacity: 0.4,
						strokeWeight: 0
					}}
				></gmap-circle>;
			}
		},

		genMarker() {
			return <gmap-marker
				icon={this.icon}
				position={this.position}
				ref={this.markerId}
				title={this.title}
				zIndex={this.active ? 1000 : ( this.zIndex || 1 )}
			></gmap-marker>;
		},

		activeSlotsProps() {
			const props = {};

			if ( this.enableGeolocation ) {
				props.distanceFromKinnunen = this.distanceFromKinnunen;
				props.geocoderRunning = this.geocoderRunning;
				props.geocodedLocation = this.geocodedLocation;
				props.geocodedLocationName = this.geocodedLocation
					? this.geocodedLocation.name
					: null;
			}

			return props;
		},

		genRootActiveSlot() {
			if ( typeof this.$scopedSlots.rootActive === "function" && this.rootActive ) {
				return this.$scopedSlots.rootActive( this.activeSlotsProps );
			}
		},

		genActiveSlot() {
			if ( typeof this.$scopedSlots.active === "function" && this.active ) {
				return this.$scopedSlots.active( this.activeSlotsProps );
			}
		}
	},

	watch: {

		// Watch this instead of active so if the same marker is re-set, it will re-open the info window
		activeMarker() {
			this.onChangeActive();
		}
	},

	methods: {
		onChangeActive() {
			if ( this.active ) {
				if ( this.geocode ) {
					this.geocode();
				}

				if ( this.autoPan ) {
					this.panTo();
				}

				this.$store.dispatch(
					`${this.storeModuleName}/setFollowable`,
					this.followable
				);
			}
		},

		panTo() {
			this.$mapPromise.then( () => {
				this.autoFollow = true;

				if ( this.position && !this.centered ) {
					this.panToActive = true;

					smoothPan(
						this.$map,
						this.spiderfiedPosition
					).then( () => this.panToActive = false );
				}
			} );
		}
	},

	created() {
		if ( !this.position ) {
			return;
		}

		this.onChangeActive();

		this.$mapPromise.then( () => {
			this.mapReady = true;

			if ( this.active ) {
				this.mapCenter = this.position;
				this.$store.dispatch( `${this.storeModuleName}/setFollowable`, this.followable );
			}

			this.$nextTick( () => {
				const marker = this.$refs[this.markerId];

				marker.$markerPromise.then( () => {
					const clickEvent = this.spiderfier ? "spider_click" : "click";

					this.google.maps.event.addListener( marker.$markerObject, clickEvent, () => {
						if ( !this.editMode ) {
							this.active = !this.active;
						}
					} );

					marker.markerType = this.markerType;

					this.$store.dispatch( `${this.storeModuleName}/addMarker`, {
						markerId: this.markerId,
						marker
					} );
				} );
			} );
		} );
	},

	render() {
		if ( !this.mapReady || !this.position ) {
			return;
		}

		return <div>
			{this.genHighlight}
			{this.genMarker}
			{this.genRootActiveSlot}
			{this.genActiveSlot}
		</div>;
	}
};
